import React, { useState, useCallback, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import DigitalTwinTableSearch from "../../../Components/DigitalTwinTableSearch";
import SeverityOfAlertsChartView from "./SeverityOfAlertsChartView";
import AutoResolvedChartView from "./AutoResolvedChartView";
import ChartDrillDownTable from "../../BmsFaults/Analytics/ChartDrillDown/ChartDrillDownTable";
import SmartAlertsDetailsSidebar from "./SmartAlertsDetailsSidebar";
import { CHART_TYPES } from "./chartConfig";
import { usePagesContent, useSearchableFields, searchFieldsKey, SMART_ALERTS_ANALYTICS_SEARCH_KEYS } from "../../../Hooks/usePagesContent";
import DeviceIDLink from "../../../Features/Table/GenericDraggableTable/Renderers/columnTypes/DeviceIDLink";
import { encryptTheParams } from "../../../Helper/QueryParams/EncryptDecrypt";
import { useNavigate } from "react-router-dom";
import { getSmartAlertsList, smartAlertsListAction } from "../../../Api/SmartAlertsAnalyticsApi";
import CODE from "../../../Static/Constants/StatusCodes";
import {
  SMART_ALERTS_META_INFO_FIELDS,
  deriveMetaInfoFields,
  pickMetaInfoValues,
} from "../../../Helper/Analytics/tableMetaInfo";

const SEARCH_FIELDS = [
  { key: "licensePlateNo", title: "Bus Number" },
  { key: "deviceID",       title: "device_id" },
  { key: "vin",            title: "Chassis Number" },
];

const SmartAlertsChartDetailsView = ({
  chartType = CHART_TYPES.SEVERITY,
  startDate: initialStartDate,
  endDate: initialEndDate,
  initialGranularity,
  globalFilters = {},
  allFleets = [],
  allOnboardingLocationsHierarchy = [],
  onBack,
}) => {
  const navigate = useNavigate();
  const pagesContent = usePagesContent();
  const searchFields = useSearchableFields(SEARCH_FIELDS, SMART_ALERTS_ANALYTICS_SEARCH_KEYS);

  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate]     = useState(initialEndDate);
  const [granularity, setGranularity] = useState(initialGranularity || "Weekly");
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [localHierarchicalLocation, setLocalHierarchicalLocation] = useState(globalFilters?.hierarchicalLocation || []);
  // Business Segment and Customer Account both resolve to fleet ids, which is what
  // becomes the fleetIDs request header.
  const [localFleetIds, setLocalFleetIds] = useState(globalFilters?.fleets || []);
  const [localAlertCategories, setLocalAlertCategories] = useState(globalFilters?.alertCategories || []);
  const [localOperationalStatus, setLocalOperationalStatus] = useState(globalFilters?.operationalStatus || []);
  const [searchTerm, setSearchTerm] = useState({ selectedDevices: [], selectedSearchColumn: null });

  const localGlobalFilters = useMemo(() => ({
    ...globalFilters,
    fleets:               localFleetIds,
    hierarchicalLocation: localHierarchicalLocation,
    alertCategories:      localAlertCategories,
    operationalStatus:    localOperationalStatus,
  }), [globalFilters, localFleetIds, localHierarchicalLocation, localAlertCategories, localOperationalStatus]);

  const chartMeta  = pagesContent?.metaInfo?.charts?.[chartType];
  const detailsTableKey = chartType === CHART_TYPES.AUTO_RESOLVED
    ? "autoResolvedDetailsPageTable"
    : "severityDetailsPageTable";
  const detailsMeta = pagesContent?.metaInfo?.table?.[detailsTableKey];
  const chartTitle  = chartMeta?.title || "Severity of Alerts Triggered vs Vehicles Impacted";

  // What this table asks the API for, and reads back, follows its configured columns.
  const metaInfoFields = useMemo(
    () => deriveMetaInfoFields(detailsMeta?.columnsConfig, SMART_ALERTS_META_INFO_FIELDS),
    [detailsMeta]
  );

  const tableConfigs = useMemo(() => detailsMeta ? {
    tableColumnsConfig: detailsMeta.columnsConfig,
    tableColumnOrder:   detailsMeta.columnOrdering,
    title: "Vehicle List",
    emptyMessage: "No vehicles found",
  } : null, [detailsMeta]);

  const getTableData = useCallback(async (_dp, pageNumber, sortBy) => {
    if (!startDate || !endDate) return { data: [], total: 0 };

    const getCount = (metrics, type) => metrics?.find((m) => m.type === type)?.value ?? 0;

    const { selectedDevices, selectedSearchColumn } = searchTerm;
    const searchFilters = selectedDevices?.length > 0 && selectedSearchColumn
      ? [{ name: selectedSearchColumn, values: selectedDevices }]
      : [];
    const combinedFilters = [...appliedFilters, ...searchFilters];

    let result;

    if (chartType === CHART_TYPES.AUTO_RESOLVED) {
      result = await getSmartAlertsList(localGlobalFilters, {
        startDate,
        endDate,
        select:    ["ResolvedAlertCount"],
        groupBy:   ["batteryID", "ResolutionType"],
        metaInfo:  metaInfoFields,
        skipPagination: false,
        pageNum:        pageNumber || 1,
        pageCount:      10,
        ...(sortBy?.length ? { sortBy } : {}),
        ...(combinedFilters.length ? { filters: combinedFilters } : {}),
      });

      if (result?.responseStatus?.code !== CODE.SUCCESS) return { data: [], total: 0 };

      const rows = (result.response?.data || []).map((row) => ({
        id:                  row.xLabel,
        ...pickMetaInfoValues(row, metaInfoFields),
        AutoResolvedCount:   getCount(row.metricsArray, "AutoResolvedCount"),
        ManualResolvedCount: getCount(row.metricsArray, "ManualResolvedCount"),
      }));

      return { data: rows, total: result.response?.total ?? rows.length };
    } else {
      result = await getSmartAlertsList(localGlobalFilters, {
        startDate,
        endDate,
        select:    ["GeneratedAlertsCount"],
        groupBy:   ["batteryID", "AlertSeverityName"],
        metaInfo:  metaInfoFields,
        skipPagination: false,
        pageNum:        pageNumber || 1,
        pageCount:      10,
        ...(sortBy?.length ? { sortBy } : {}),
        ...(combinedFilters.length ? { filters: combinedFilters } : {}),
      });

      if (result?.responseStatus?.code !== CODE.SUCCESS) return { data: [], total: 0 };

      const rows = (result.response?.data || []).map((row) => ({
        id:                row.xLabel,
        ...pickMetaInfoValues(row, metaInfoFields),
        Critical:          getCount(row.metricsArray, "Critical"),
        "High Severity":   getCount(row.metricsArray, "High Severity") || getCount(row.metricsArray, "High"),
        Warning:           getCount(row.metricsArray, "Warning") || getCount(row.metricsArray, "Medium") || 0,
      }));

      return { data: rows, total: result.response?.total ?? rows.length };
    }
  }, [chartType, startDate, endDate, localGlobalFilters, searchTerm, appliedFilters, metaInfoFields]);

  const applyTableAction = useCallback(async (rowSelectionKeys, actionType, selectAll, params) => {
    const { columns: tCols, emailTo, emailCc, subject, body } = params;
    const csvColumns = tCols
      .filter((col) => col.dataIndex !== "actions")
      .map((col) => ({
        dataIndex:   col.dataIndex === "id" ? "xLabel" : col.dataIndex,
        headerLabel: col.headerLabel || col.title,
      }));

    const { selectedDevices, selectedSearchColumn } = searchTerm;
    const searchFilters = selectedDevices?.length > 0 && selectedSearchColumn
      ? [{ name: selectedSearchColumn, values: selectedDevices }]
      : [];
    const combinedFilters = [...appliedFilters, ...searchFilters];

    const commonParams = {
      actionType:  actionType === "download" ? "download-csv" : "mail",
      startDate,
      endDate,
      metaInfo:    metaInfoFields,
      ...(combinedFilters.length ? { filters: combinedFilters } : {}),
      selectAll:   selectAll || rowSelectionKeys.length === 0,
      resourceIDs: selectAll ? [] : rowSelectionKeys,
      columns:     csvColumns,
      columnOrder: csvColumns.map((c) => c.headerLabel),
      ...(actionType === "mail" ? { emailTo, emailCc, subject, body } : {}),
    };

    let result;
    if (chartType === CHART_TYPES.AUTO_RESOLVED) {
      result = await smartAlertsListAction(localGlobalFilters, {
        ...commonParams,
        select:   ["ResolvedAlertCount"],
        groupBy:  ["batteryID", "ResolutionType"],
        fileName: "auto_resolved_vehicles",
      });
    } else {
      result = await smartAlertsListAction(localGlobalFilters, {
        ...commonParams,
        select:   ["GeneratedAlertsCount"],
        groupBy:  ["batteryID", "AlertSeverityName"],
        fileName: "severity_vehicles",
      });
    }
    if (actionType === "download") return result?.response?.data || [];
  }, [chartType, startDate, endDate, localGlobalFilters, searchTerm, appliedFilters, metaInfoFields]);

  const handleSearch = useCallback((selectedDevices, selectedSearchColumn) => {
    setSearchTerm({ selectedDevices, selectedSearchColumn });
  }, []);

  const onBatteryIDClick = useCallback((record) => {
    encryptTheParams({
      batteryID: record.id,
      deviceID: record.deviceID,
      batterySearchedGlobal: "false",
      prevLink: "/",
      navigateFromTab: true,
      tabValue: "alerts",
      alertsSubTab: "platform-alerts",
    }, navigate, true);
  }, [navigate]);

  const customRenderers = useMemo(() => ({
    "device-id-link": (record, config) => (
      <DeviceIDLink record={record} config={config} onTextClick={() => onBatteryIDClick(record)} />
    ),
  }), [onBatteryIDClick]);

  if (!tableConfigs) return null;

  return (
    <Box sx={{ display: "flex", gap: 3, height: "100%", position: "relative" }}>
      {/* Main content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          width: isSidebarExpanded ? "calc(100% - 320px)" : "100%",
          minWidth: 0,
          transition: "width 0.3s ease-in-out",
        }}
      >
        {/* Back button */}
        <Box
          onClick={onBack}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            cursor: "pointer",
            width: "fit-content",
            "&:hover": {
              "& .MuiTypography-root": { color: "#6D72F6 !important" },
              "& .MuiSvgIcon-root":    { color: "#6D72F6" },
            },
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: 20, color: "#2E2E2E" }} />
          <Typography variant="fw500s14lh24c2E2E2E">Back</Typography>
        </Box>

        {/* Search bar */}
        <DigitalTwinTableSearch
          onSearch={handleSearch}
          containerWidth="80%"
          key={searchFieldsKey(searchFields)}
          searchableFields={searchFields}
        />

        {/* Chart card */}
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            borderRadius: "8px",
            padding: "24px 22px",
            border: "1px solid #E6E6E6",
          }}
        >
          <Box sx={{ mb: 1 }}>
            <Typography variant="fw600s16lh24c444B51" sx={{ color: "#2E2E2E" }}>
              {chartTitle}
            </Typography>
          </Box>
          {chartType === CHART_TYPES.AUTO_RESOLVED ? (
            <AutoResolvedChartView
              isSidebarExpanded={isSidebarExpanded}
              startDate={startDate}
              endDate={endDate}
              granularity={granularity}
              globalFilters={localGlobalFilters}
              filters={appliedFilters}
              searchTerm={searchTerm}
            />
          ) : (
            <SeverityOfAlertsChartView
              isSidebarExpanded={isSidebarExpanded}
              startDate={startDate}
              endDate={endDate}
              granularity={granularity}
              globalFilters={localGlobalFilters}
              filters={appliedFilters}
              searchTerm={searchTerm}
            />
          )}
        </Box>

        {/* Vehicle List table — columns driven entirely by pagemeta */}
        <ChartDrillDownTable
          tableConfigs={tableConfigs}
          getTableData={getTableData}
          applyTableAction={applyTableAction}
          getCSVFileName={() => chartType === CHART_TYPES.AUTO_RESOLVED ? "auto_resolved_vehicles" : "severity_vehicles"}
          selectedDataPoint={null}
          pageSize={10}
          customRenderers={customRenderers}
          isOpen={true}
        />
      </Box>

      {/* Settings sidebar */}
      <SmartAlertsDetailsSidebar
        isExpanded={isSidebarExpanded}
        onToggle={() => setIsSidebarExpanded((v) => !v)}
        chartType={chartType}
        startDate={startDate}
        endDate={endDate}
        onDateChange={({ startDate: sd, endDate: ed }) => { setStartDate(sd); setEndDate(ed); }}
        granularity={granularity}
        onGranularityChange={setGranularity}
        allFleets={allFleets}
        allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
        selectedFleetIds={localFleetIds || []}
        selectedHierarchicalLocation={localHierarchicalLocation}
        initialAlertCategories={localAlertCategories}
        initialOperationalStatus={localOperationalStatus}
        onFiltersChange={setAppliedFilters}
        onHierarchicalLocationChange={setLocalHierarchicalLocation}
        onFleetIdsChange={setLocalFleetIds}
        onAlertCategoriesChange={setLocalAlertCategories}
        onOperationalStatusChange={setLocalOperationalStatus}
      />
    </Box>
  );
};

export default SmartAlertsChartDetailsView;
